import { useMemo, useState } from "react";
import { config, renderTokens } from "@/config/eventConfig";
import {
  scorePrompt,
  detectPII,
  SCAFFOLDS,
  ELEMENT_ORDER,
} from "@/lib/rtcfScorer";

const META = {
  role: config.rtcf.elements[0],
  task: config.rtcf.elements[1],
  context: config.rtcf.elements[2],
  format: config.rtcf.elements[3],
};

const PLACEHOLDERS = {
  role: "You are a...",
  task: "Do this one thing...",
  context: "The facts it needs...",
  format: "Shape of the output...",
};

const Bar = ({ elementKey, score, miss, showMiss }) => (
  <div data-testid={`bar-${elementKey}`}>
    <div className="flex justify-between items-baseline mb-1">
      <span className="mono-label text-ink/70">{META[elementKey].name}</span>
      <span className="mono-label text-ink/40">{score}/3</span>
    </div>
    <div className="flex gap-1">
      {[0, 1, 2].map((i) => (
        <div
          key={i}
          className={`h-2 flex-1 ${i < score ? "bg-teal" : "bg-sand"}`}
        />
      ))}
    </div>
    {showMiss && miss && (
      <p data-testid={`miss-${elementKey}`} className="text-sm text-ink/60 mt-2">
        {miss}
      </p>
    )}
  </div>
);

export const PromptChallenge = () => {
  const [fields, setFields] = useState({ role: "", task: "", context: "", format: "" });
  const [result, setResult] = useState(null);
  const [pii, setPii] = useState(null);
  const [showRubric, setShowRubric] = useState(false);
  const [copied, setCopied] = useState(false);

  const live = useMemo(() => scorePrompt(fields), [fields]);
  const shown = result || live;

  const assembled = ELEMENT_ORDER.map((k) => fields[k].trim())
    .filter(Boolean)
    .join("\n\n");

  const update = (key) => (e) => {
    setFields((f) => ({ ...f, [key]: e.target.value }));
    setResult(null); // editing returns the bars to a live meter
    setPii(null);
  };

  const handleScore = () => {
    const p = detectPII(fields);
    if (p.blocked) {
      setPii(p);
      setResult(null);
      return;
    }
    setPii(null);
    setResult(scorePrompt(fields));
  };

  const scoreAnyway = () => {
    setPii(null);
    setResult(scorePrompt(fields)); // override clears the block for this attempt only
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(assembled);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // clipboard unavailable
    }
  };

  const assistantName = config.tools.assistant.name;

  return (
    <div data-testid="prompt-challenge">
      {/* Four fields, 2x2, matching the teaching screen */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {ELEMENT_ORDER.map((key) => (
          <div key={key} className="bg-white border border-sand p-5">
            <div className="flex items-center gap-3 mb-3">
              <span className="w-8 h-8 bg-teal text-white font-display text-lg leading-none flex items-center justify-center shrink-0">
                {META[key].letter}
              </span>
              <span className="font-semibold">{META[key].name}</span>
            </div>
            <textarea
              data-testid={`field-${key}`}
              value={fields[key]}
              onChange={update(key)}
              placeholder={PLACEHOLDERS[key]}
              rows={3}
              className="w-full resize-none bg-cream/40 border border-sand focus:border-orange focus:outline-none px-3 py-2 text-ink"
            />
          </div>
        ))}
      </div>

      {/* Assembled prompt */}
      <div className="mt-6 bg-white border border-sand p-5">
        <div className="flex items-center justify-between mb-2">
          <span className="mono-label text-ink/50">Your prompt</span>
          <button
            data-testid="copy-prompt"
            onClick={copy}
            className="text-sm border border-ink/20 px-4 py-1.5 hover:border-orange hover:text-orange-dark transition-colors duration-150"
          >
            {copied ? "Copied" : "Copy prompt"}
          </button>
        </div>
        <pre
          data-testid="assembled-prompt"
          className="whitespace-pre-wrap font-body text-ink/80 min-h-[3rem]"
        >
          {assembled || "Your four fields will assemble here as you type."}
        </pre>
      </div>

      <p className="text-sm text-ink/60 mt-3">
        Scoring happens on your own device. Nothing you type here is sent
        anywhere or saved.
      </p>

      {/* Live meter */}
      <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-6">
        {ELEMENT_ORDER.map((key) => (
          <Bar
            key={key}
            elementKey={key}
            score={shown.scores[key]}
            miss={shown.misses[key]}
            showMiss={!!result}
          />
        ))}
      </div>

      {/* Actions */}
      <div className="mt-6 flex items-center gap-4">
        <button
          data-testid="score-button"
          onClick={handleScore}
          className="px-6 py-3 bg-orange hover:bg-orange-dark text-white transition-colors duration-150"
        >
          Score my prompt
        </button>
        {result && (
          <span
            data-testid="score-chip"
            className="mono-label border border-ink/20 px-3 py-2"
          >
            Score {result.total} / 12
          </span>
        )}
      </div>

      {/* PII guard */}
      {pii && (
        <div data-testid="pii-callout" className="callout-warning mt-6">
          <p className="font-semibold text-orange-dark mb-1">
            Stop, that looks like real data
          </p>
          <p className="text-ink/70 mb-2">
            We spotted {pii.kinds.join(", ")} in what you typed.
          </p>
          <p className="text-ink/70 mb-3">
            Swap it for a made-up example with the same shape and score it again.
            This is the data rule from this morning, doing its job.
          </p>
          <button
            data-testid="pii-override"
            onClick={scoreAnyway}
            className="text-sm underline underline-offset-4 text-ink/60 hover:text-orange-dark transition-colors duration-150"
          >
            This is a made-up example
          </button>
        </div>
      )}

      {/* Reveal after scoring */}
      {result && (
        <div className="mt-8">
          {result.pass ? (
            <p data-testid="verdict" className="text-lg text-ink">
              {renderTokens(
                "Good enough to run. Copy it into {assistant.name} and see what you get."
              )}
            </p>
          ) : (
            <div data-testid="one-more-pass">
              <p className="mono-label text-orange-dark mb-3">One more pass</p>
              <div className="border-2 border-orange p-6">
                <div className="flex items-center gap-3 mb-2">
                  <span className="w-8 h-8 bg-orange text-white font-display text-lg leading-none flex items-center justify-center shrink-0">
                    {META[result.weakest].letter}
                  </span>
                  <span className="font-semibold">
                    {META[result.weakest].name}
                  </span>
                </div>
                <p className="text-ink/70 mb-4">{result.misses[result.weakest]}</p>
                <div className="border-l-2 border-sand pl-4">
                  <p data-testid="scaffold" className="italic text-ink/70">
                    {SCAFFOLDS[result.weakest]}
                  </p>
                </div>
              </div>
              <p className="text-sm text-ink/50 mt-3">
                Fill this in yourself, then score again. Completing it teaches
                more than a rewrite would.
              </p>
            </div>
          )}
        </div>
      )}

      {/* Honest, expandable rubric */}
      <div className="mt-8 border-t border-sand pt-5">
        <button
          data-testid="rubric-toggle"
          onClick={() => setShowRubric((v) => !v)}
          className="text-sm underline underline-offset-4 text-ink/60 hover:text-orange-dark transition-colors duration-150"
        >
          {showRubric ? "Hide the scoring criteria" : "See the scoring criteria"}
        </button>
        {showRubric && <Rubric assistantName={assistantName} />}
      </div>
    </div>
  );
};

const Rubric = () => (
  <div data-testid="rubric" className="mt-5 space-y-5 text-sm text-ink/80">
    <p className="text-ink/60">
      This runs entirely on your device. These are the exact checks, no hidden
      model, no server.
    </p>
    <RubricBlock
      title="Role, 0 to 3"
      items={[
        "+1 non-empty and more than two words",
        "+1 contains a job noun (officer, manager, coordinator, writer, teacher, fundraiser, grant writer, …)",
        "+1 contains a qualifier (a number of years, 'in education', 'for nonprofits', 'who has', experienced, senior, veteran)",
        "Capped at 1 if the field is only a generic word (assistant, helper, AI, bot, expert, professional)",
      ]}
    />
    <RubricBlock
      title="Task, 0 to 3"
      items={[
        "+1 non-empty",
        "+1 contains an imperative verb (draft, write, summarise, list, outline, compare, translate, extract, plan, generate, edit, turn, map)",
        "+1 a concrete object follows the verb",
        "Capped at 2 if two or more verbs appear, or a hedge (help me, assist with, do something about)",
      ]}
    />
    <RubricBlock
      title="Context, 0 to 3 (one point per category detected, max 3)"
      items={[
        "Who you serve: a number near a people noun (students, families, youth, participants, clients)",
        "Audience: funder, board, donor, parent, staff, agency, committee",
        "A constraint: a word limit, a deadline, must, must not, cannot",
        "Source material: attached, the document, our past, this proposal",
        "An organization-specific token: your organization name, a program name, or a proper noun",
        "Tone: warm, formal, plain, celebratory, urgent, evidence-led",
        "Capped at 2 if no organization-specific token is found, no matter how many other categories are present.",
      ]}
    />
    <RubricBlock
      title="Format, 0 to 3"
      items={[
        "+1 non-empty",
        "+1 contains a structure noun (bullets, table, paragraphs, sections, headings, list, numbered, email, memo)",
        "+1 contains a bound (a number of words/paragraphs/items/questions, under, no more than, at most)",
      ]}
    />
    <RubricBlock
      title="Data guard (also local)"
      items={[
        "Blocks scoring on an email pattern, a phone pattern, a run of five or more digits, or two consecutive capitalised words that are not a known organization, sentence-start, or place name.",
        "False positives are fine, you can override once. A false negative is the thing we are preventing.",
      ]}
    />
  </div>
);

const RubricBlock = ({ title, items }) => (
  <div>
    <p className="mono-label text-ink/60 mb-2">{title}</p>
    <ul className="list-disc pl-5 space-y-1">
      {items.map((it, i) => (
        <li key={i}>{it}</li>
      ))}
    </ul>
  </div>
);
